// The Add RPM Reading form: the measurement fields follow the device type, so a coordinator logs
// "Systolic / Diastolic" for a BP cuff or "SpO2 %" for a pulse oximeter instead of a bare "Value".
// recordChoice: "type" lets Prism record which option was picked (a fixed list, never patient data).

export const READING_TYPES = [
  { value: "blood_pressure", label: "Blood pressure" },
  { value: "pulse_ox", label: "Pulse oximeter" },
  { value: "heart_rate", label: "Pulse" },
  { value: "weight", label: "Weight (scale)" },
  { value: "glucose", label: "Blood glucose" },
  { value: "temperature", label: "Temperature" },
  { value: "respiratory_rate", label: "Respiratory rate" },
];

export const READING_TYPE_LABELS = Object.fromEntries(READING_TYPES.map((t) => [t.value, t.label]));

const MEASUREMENTS = {
  blood_pressure: [
    { name: "systolic", label: "Systolic (mmHg)", type: "number", required: true, inputProps: { min: 50, max: 260, step: 1 } },
    { name: "diastolic", label: "Diastolic (mmHg)", type: "number", required: true, inputProps: { min: 30, max: 160, step: 1 } },
  ],
  pulse_ox: [
    { name: "value", label: "Oxygen saturation (SpO2 %)", type: "number", required: true, inputProps: { min: 50, max: 100, step: 1 } },
    { name: "pulse", label: "Pulse (bpm)", type: "number", inputProps: { min: 20, max: 250, step: 1 } },
  ],
  heart_rate: [
    { name: "value", label: "Pulse (bpm)", type: "number", required: true, inputProps: { min: 20, max: 250, step: 1 } },
  ],
  weight: [
    { name: "value", label: "Weight (lbs)", type: "number", required: true, inputProps: { min: 1, max: 1000, step: 0.1 } },
  ],
  glucose: [
    { name: "value", label: "Blood glucose (mg/dL)", type: "number", required: true, inputProps: { min: 10, max: 800, step: 1 } },
  ],
  temperature: [
    { name: "value", label: "Temperature (°F)", type: "number", required: true, inputProps: { min: 90, max: 110, step: 0.1 } },
  ],
  respiratory_rate: [
    { name: "value", label: "Respiratory rate (breaths/min)", type: "number", required: true, inputProps: { min: 4, max: 60, step: 1 } },
  ],
};

const MEASUREMENT_NAMES = ["value", "systolic", "diastolic", "pulse"];

export function rpmReadingFields(readingType) {
  return [
    { name: "reading_type", label: "Reading Type", type: "select", options: READING_TYPES, required: true, recordChoice: "type" },
    ...(MEASUREMENTS[readingType] || []),
    { name: "reading_date", label: "Reading Date/Time", type: "datetime-local", required: true },
    { name: "device_id", label: "Device ID", type: "text" },
    { name: "alert_level", label: "Alert Level", type: "select", options: ["normal", "low", "high", "critical"] },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
}

// Picking another device type clears the numbers typed for the old one
export function changeReadingType(data, readingType) {
  const next = { ...data, reading_type: readingType };
  MEASUREMENT_NAMES.forEach((name) => delete next[name]);
  return next;
}

// "2026-10-14T09:30" in the browser's own time zone, for the datetime-local field
export function nowLocalInput(now = new Date()) {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

// The readings to save. The table has no pulse column, so a pulse oximeter's pulse is saved
// as its own Pulse reading with the same time and device.
export function rpmReadingPayloads(payload) {
  const { pulse, ...reading } = payload;
  const readings = [reading];
  if (reading.reading_type === "pulse_ox" && pulse !== null && pulse !== undefined && pulse !== "") {
    readings.push({
      patient_id: reading.patient_id,
      reading_type: "heart_rate",
      value: Number(pulse),
      reading_date: reading.reading_date,
      device_id: reading.device_id ?? null,
      notes: "From pulse oximeter",
    });
  }
  return readings;
}
